import {
    Component,
    EventEmitter,
    Input,
    Output,
    OnChanges,
    SimpleChanges,
    inject,
} from '@angular/core';
import { AuthService } from '@services';
import {
    DxButtonModule,
    DxDropDownButtonModule,
    DxToolbarModule,
} from 'devextreme-angular';
import { AccessType, InvoiceAction, InvoiceStatus, PermissionType } from '@services/web-api-client';

interface MenuAction {
    id: InvoiceAction;
    text: string;
    icon: string;
}

@Component({
    selector: 'app-invoice-details-toolbar',
    standalone: true,
    imports: [DxToolbarModule, DxButtonModule, DxDropDownButtonModule],
    templateUrl: './invoice-details-toolbar.component.html',
})
export class InvoiceDetailsToolbarComponent implements OnChanges {
    @Input() number: string | null = null;
    @Input() status: InvoiceStatus | undefined;
    @Input() actions: InvoiceAction[] | null = null;
    @Input() isReadOnly: boolean = false;
    @Input() isSubmitting: boolean = false;
    @Input() isLoading: boolean = false;
    @Input() isExporting: boolean = false;

    @Output() cancelClick = new EventEmitter<void>();
    @Output() save = new EventEmitter<InvoiceAction>();
    @Output() exportClick = new EventEmitter<void>();

    protected auth = inject(AuthService);
    protected moreMenuActions: MenuAction[] = [];
    public InvoiceAction = InvoiceAction;

    private readonly allMenuActions: MenuAction[] = [
        {
            id: InvoiceAction.BackToVendor,
            text: 'Send Back',
            icon: 'fa-light fa-arrow-left',
        },
        {
            id: InvoiceAction.BackToL1,
            text: 'Back to L1',
            icon: 'fa-light fa-arrow-left',
        },
        {
            id: InvoiceAction.Investigate,
            text: 'Investigate',
            icon: 'fa-thin fa-circle-info',
        },
        {
            id: InvoiceAction.OnHold,
            text: 'On Hold',
            icon: 'fa-thin fa-circle-pause',
        },
        {
            id: InvoiceAction.UndoToPendingAP,
            text: 'Undo to Pending AP',
            icon: 'fa-light fa-arrow-left',
        },
        {
            id: InvoiceAction.Decline,
            text: 'Decline',
            icon: 'fa-light fa-xmark',
        },
    ];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['status'] || changes['actions']) {
            this.buildMenuActions();
        }
    }

    private buildMenuActions(): void {
        if (this.isLoading) {
            this.moreMenuActions = [];
            return;
        }

        this.moreMenuActions = this.allMenuActions.filter((action) =>
            this.canShowButton(action.id),
        );
    }

    canShowButton(action: InvoiceAction): boolean {
        if (this.isLoading) return false;

        if (!this.number && !this.isReadOnly) {
            return ((action === InvoiceAction.Submit && this.canSubmit) || action === InvoiceAction.SaveAsDraft);
        }

        if (this.number && !this.isReadOnly && action === InvoiceAction.Save) {
            return true;
        }

        return this.actions?.includes(action) ?? false;
    }

    get canSubmit (): boolean {
        return this.auth.checkPermission(PermissionType.InvoiceSubmit, AccessType.Edit);
    }

    get canExport(): boolean {
        return !this.isLoading && !!this.number && !!this.status && this.status !== InvoiceStatus.Draft;
    }

    get showMoreMenu(): boolean {
        return this.number !== null && this.moreMenuActions.length > 0;
    }

    onMenuActionClick(e: { itemData?: MenuAction }): void {
        if (!e.itemData) return;

        this.onButtonClick(e.itemData.id as InvoiceAction);
    }

    onButtonClick(status: InvoiceAction) {
        this.save.emit(status);
    }
}
